<template>
	<div ref="createMeetingRef" id="text-test">
		<v-dialog
			v-model="modalStatus"
			:max-width="$vuetify.breakpoint.mobile ? undefined : '780'"
			:fullscreen="$vuetify.breakpoint.mobile"
			scrollable
			persistent
		>
			<v-card class="modal-card" elevation="0">
				<!-- Header -->
				<div class="modal-header">
					<div class="modal-header-left">
						<div class="modal-icon">
							<v-icon color="white" size="16">mdi-calendar-account-outline</v-icon>
						</div>
						<div>
							<p class="modal-title">Crear Reunión/Minuta</p>
							<p class="modal-subtitle">
								<span>{{ $store.userStore.state.userInfo.name }}</span>
								<span class="separator">·</span>
								<span>{{ $store.projectsStore.state.selectedProject.name }}</span>
							</p>
						</div>
					</div>
					<div class="modal-header-actions">
						<button class="header-btn minimize" @click="minimizeForm" title="Minimizar">
							<v-icon size="14" color="white">mdi-window-minimize</v-icon>
						</button>
						<button class="header-btn close" @click="closeModalSelf()" title="Cerrar">
							<v-icon size="14" color="white">mdi-close</v-icon>
						</button>
					</div>
				</div>

				<v-card-text class="modal-body pa-0">
					<v-form ref="form" v-model="validform">

						<!-- Título -->
						<div class="form-section">
							<v-text-field
									v-model="newMeetingForm.title"
									placeholder="Título de la reunión"
									outlined
									required
									hide-details
									:rules="requiredRule"
									:dense="true"
									class="title-input"
							/>
						</div>

						<!-- Descripción -->
						<div class="form-section">
							<label class="field-label">Descripción</label>
							<InteractionEditor
								v-model="newMeetingForm.description"
								item_type="meeting"
								@paste-image="onPasteImage"
								@mentions-updated="onMentionsUpdated"
							/>
							<!-- Adjuntos -->
							<div class="attachments-row">
								<div class="previews">
									<div v-for="(file, index) in filesPreview" :key="index" class="preview-item">
										<div v-if="file.src" class="preview-img-wrap">
											<button class="preview-remove" type="button" @click="removeAttachment(file, index)">
												<v-icon size="10" color="white">mdi-close</v-icon>
											</button>
											<img :src="file.src" class="preview-thumb"/>
										</div>
										<div v-else class="preview-file">
											<v-icon size="14" color="#5E85EF">mdi-file-outline</v-icon>
											<span>{{ file.name }}</span>
											<button type="button" @click="removeAttachment(file, index)"><v-icon size="12" color="#9CA3AF">mdi-close</v-icon></button>
										</div>
									</div>
								</div>
								<button class="attach-btn" type="button" @click="onButtonClick">
									<v-icon size="14" color="#5E85EF">mdi-paperclip</v-icon>
									Adjuntar
								</button>
								<input ref="uploader" multiple class="d-none" type="file" @change="onFilesChanged">
							</div>
						</div>

						<!-- Grid: Fecha/Hora inicio + Fecha/Hora término -->
						<div class="form-section">
							<div class="fields-grid four-col">

								<!-- Fecha inicio -->
								<div class="field-group">
									<label class="field-label">Fecha inicio</label>
									<v-menu v-model="startDateMenu" :close-on-content-click="false" max-width="290">
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												:value="formatDatePart(startDatePart)"
												placeholder="Seleccionar"
												prepend-inner-icon="mdi-calendar"
												readonly
												v-bind="attrs"
												v-on="on"
												outlined
												dense
												hide-details
												required
												:rules="dateRequiredRule"
												class="remove-bottom-details"
											/>
										</template>
										<v-date-picker locale="es" v-model="startDatePart" :min="todayDate" no-title />
									</v-menu>
								</div>

								<!-- Hora inicio -->
								<div class="field-group">
									<label class="field-label">Hora inicio</label>
									<SimpleTimePicker
										v-model="startTimePart"
										:min-time="startMinTime"
										placeholder="Seleccionar"
									/>
								</div>

								<!-- Fecha término -->
								<div class="field-group">
									<label class="field-label">Fecha término</label>
									<v-menu v-model="endDateMenu" :close-on-content-click="false" max-width="290">
										<template v-slot:activator="{ on, attrs }">
											<v-text-field
												:value="formatDatePart(endDatePart)"
												placeholder="Sin fecha"
												prepend-inner-icon="mdi-calendar"
												readonly
												v-bind="attrs"
												v-on="on"
												outlined
												dense
												hide-details
												class="remove-bottom-details"
											/>
										</template>
										<v-date-picker locale="es" v-model="endDatePart" :min="minEndDate" no-title />
									</v-menu>
								</div>

								<!-- Hora término -->
								<div class="field-group">
									<label class="field-label">Hora término</label>
									<SimpleTimePicker
										v-model="endTimePart"
										:min-time="endMinTime"
										placeholder="Sin hora"
										:disabled="!endDatePart"
									/>
								</div>

							</div>
							<p v-if="endDateError" class="field-error">{{ endDateError }}</p>
						</div>

						<!-- Modalidad -->
						<div class="form-section">
							<label class="field-label">Modalidad</label>
							<div class="modality-group">
								<button
									class="modality-btn"
									:class="{ active: meetingModality === 'presencial' }"
									type="button"
									@click="meetingModality = 'presencial'"
								>
									<v-icon size="15" :color="meetingModality === 'presencial' ? '#fff' : '#5E85EF'">mdi-map-marker-outline</v-icon>
									Presencial
								</button>
								<button
									class="modality-btn"
									:class="{ active: meetingModality === 'virtual' }"
									type="button"
									@click="meetingModality = 'virtual'"
								>
									<v-icon size="15" :color="meetingModality === 'virtual' ? '#fff' : '#5E85EF'">mdi-video-outline</v-icon>
									Virtual
								</button>
							</div>
							<transition name="fade-slide">
								<div v-if="meetingModality === 'presencial'" key="address" class="modality-field">
									<v-text-field
										v-model="newMeetingForm.address"
										placeholder="Dirección de la reunión"
										prepend-inner-icon="mdi-map-marker-outline"
										outlined
										dense
										hide-details
									/>
								</div>
								<div v-else key="link" class="modality-field">
									<v-text-field
										v-model="newMeetingForm.link"
										placeholder="Link de videollamada"
										prepend-inner-icon="mdi-video-outline"
										outlined
										dense
										hide-details
									/>
								</div>
							</transition>
						</div>

						<!-- Grid: Responsable + Participantes -->
						<div class="form-section">
							<div class="fields-grid two-col">

								<!-- Responsable -->
								<div class="field-group">
									<label class="field-label">Responsable</label>
									<v-autocomplete
										v-model="newMeetingForm.responsible_id"
										:loading="$store.usersStore.state.loadingUserList"
										:items="getFinalUserList()"
										:search-input.sync="searchUsers"
										item-text="name"
										item-value="id"
										:filter="usersFilter"
										outlined
										dense
										hide-details
										placeholder="Seleccionar responsable"
									>
										<template v-slot:item="data">
											<v-list-item-avatar size="28">
												<img v-if="data.item.info && data.item.info.avatar" :src="data.item.info.avatar">
												<v-icon v-else size="20">mdi-account</v-icon>
											</v-list-item-avatar>
											<v-list-item-content>
												<v-list-item-title>{{ data.item.name }} <span class="light-blue-text">{{ data.item.projectjob.userjobtype.name }}</span></v-list-item-title>
												<v-list-item-subtitle>{{ data.item.email }}</v-list-item-subtitle>
											</v-list-item-content>
										</template>
									</v-autocomplete>
								</div>

								<!-- Participantes -->
								<div class="field-group">
									<label class="field-label">Participantes</label>
									<v-autocomplete
										v-model="participants"
										:loading="$store.usersStore.state.loadingUserList"
										:items="getFinalUserList(true)"
										:search-input.sync="searchUsersParticipants"
										item-text="name"
										item-value="id"
										:filter="usersFilter"
										outlined
										dense
										hide-details
										multiple
										hide-selected
										placeholder="Agregar participantes"
									>
										<template v-slot:item="data">
											<v-list-item-avatar size="28">
												<img v-if="data.item.info && data.item.info.avatar" :src="data.item.info.avatar">
												<v-icon v-else size="20">mdi-account</v-icon>
											</v-list-item-avatar>
											<v-list-item-content>
												<v-list-item-title>{{ data.item.name }} <span class="light-blue-text">{{ data.item.projectjob.userjobtype.name }}</span></v-list-item-title>
												<v-list-item-subtitle>{{ data.item.email }}</v-list-item-subtitle>
											</v-list-item-content>
										</template>
										<template v-slot:selection="{}"></template>
									</v-autocomplete>
								</div>

							</div>

							<!-- Tarjetas de participantes -->
							<div v-if="participants.length" class="participant-list mt-2">
								<div v-for="id in participants" :key="id" class="participant-card">
									<template v-if="getUserByID(id)">
										<div class="participant-left">
											<div class="p-avatar">
												<img v-if="getUserByID(id).info && getUserByID(id).info.avatar" :src="getUserByID(id).info.avatar" />
												<span v-else>{{ initials(getUserByID(id).name) }}</span>
											</div>
											<div class="p-info">
												<span class="p-name">{{ getUserByID(id).name }}</span>
												<span class="p-role" v-if="getUserByID(id).projectjob && getUserByID(id).projectjob.userjobtype">{{ getUserByID(id).projectjob.userjobtype.name }}</span>
											</div>
										</div>
										<div class="participant-right">
											<button class="p-remove-btn" type="button" @click="removeParticipantByID(id)">
												<v-icon size="14" color="#9CA3AF">mdi-trash-can-outline</v-icon>
											</button>
										</div>
									</template>
								</div>
							</div>
						</div>

						<!-- Footer -->
						<div class="modal-footer">
							<div />
							<div class="footer-actions">
								<button class="btn-cancel" type="button" @click="closeModalSelf()">Cancelar</button>
								<button
									v-if="checkPermissions(['create_meetings'])"
									class="btn-confirm"
									:class="{ canceling: inCancelTime }"
									type="button"
									:disabled="loadingData"
									@click="preCreateMeeting()"
								>
									<v-progress-circular v-if="loadingData" :size="14" :width="2" color="white" indeterminate class="mr-1" />
									{{ confirmButtonText }}
								</button>
								<button
									v-else
									class="btn-confirm"
									:class="{ canceling: inCancelTime }"
									type="button"
									:disabled="loadingData"
									@click="saveDraftAndClose()"
								>
									<v-progress-circular v-if="loadingData" :size="14" :width="2" color="white" indeterminate class="mr-1" />
									Guardar Borrador
								</button>
							</div>
						</div>

					</v-form>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import moment from 'moment'
import { v4 as uuidv4 } from 'uuid'
import { create, getMeeting } from '@/helpers/api/meetings'
import { deleteFile } from '@/helpers/api/fileManager'
import { create as createDraftAPI, update as updateDraftAPI } from '@/helpers/api/drafts'
import EventBus from '@/helpers/event_bus'
import InteractionEditor from '@/components/Globals/Helpers/InteractionEditor'
import SimpleTimePicker from '@/components/Globals/Helpers/SimpleTimePicker'

const DATETIME_FORMAT = 'YYYY-MM-DD HH:mm'

export default {
	name: 'CreateMeetingModal',
	components: { InteractionEditor, SimpleTimePicker },
	props: {
		modalStatus:{ type: Boolean, default: false },
		closeModal: { type: Function, required: true },
		draftToLoad: { type: Object, default: null }
	},
	watch: {
		searchUsers(val) { if(val && val.length > 5) this.searchUsersLocal(val) },
		searchUsersParticipants(val) { if(val && val.length > 5) this.searchUsersLocal(val) },
		draftToLoad(newVal) { if(newVal) this.loadDraft(newVal) },
		newMeetingForm: { handler() { this.handleFormChange() }, deep: true },
		participants: { handler() { this.handleFormChange() }, deep: true },
		meetingModality() { this.handleFormChange() },
		startDatePart(newVal) { if(newVal) this.startDateMenu = false; this.syncStart() },
		startTimePart() { this.syncStart() },
		endDatePart(newVal) { if(newVal) this.endDateMenu = false; this.syncEnd() },
		endTimePart() { this.syncEnd() }
	},
	data: () => ({
		validform: true,
		newMeetingForm: {
			title: '', description: '', start: null, end: null,
			responsible_id: null, address: '', link: ''
		},
		requiredRule: [v => !!v || 'Campo requerido'],
		participants: [],
		meetingModality: 'virtual',
		startDateMenu: false,
		endDateMenu: false,
		startDatePart: null,
		startTimePart: null,
		endDatePart: null,
		endTimePart: null,
		endDateError: '',
		files: [],
		filesPreview: [],
		loadingData: false,
		loadingUsers: false,
		usersToSelect: [],
		searchUsers: null,
		searchUsersParticipants: null,
		cancelTimer: 7,
		inCancelTime: false,
		cancelInterval: null,
		confirmButtonText: 'Confirmar',
		draftId: null,
		draftCreated: false,
		draftUpdateInterval: 20,
		draftSaveTimer: null,
		handleFormChangeDebounced: null,
		isResetting: false,
		newInteractionMentions: []
	}),
	computed: {
		todayDate() { return moment().format('YYYY-MM-DD') },
		minEndDate() { return this.startDatePart || this.todayDate },
		dateRequiredRule() { return [() => !!this.startDatePart || 'Campo requerido'] },
		startMinTime() {
			return this.startDatePart === this.todayDate ? moment().format('HH:mm') : null
		},
		endMinTime() {
			if(this.endDatePart && this.startDatePart && this.endDatePart === this.startDatePart) {
				return this.startTimePart || '00:00'
			}
			return null
		}
	},
	mounted() {
		this.handleFormChangeDebounced = this.debounce(() => this.checkAndCreateDraft(), 1000)
		if(this.$store.consultasStore.state.borradorMeeting) {
			this.newMeetingForm = {...this.newMeetingForm, ...this.$store.consultasStore.state.borradorMeeting}
			this.$store.consultasStore.commit('setBorradorMeeting', null)
		}
		if(this.$store.usersStore.state.usersList.length === 0) this.getUsersList()
		if(!this.checkPermissions(['assign_responsable_meeting'])) this.newMeetingForm.responsible_id = this.$store.userStore.state.userInfo.id
		if(this.draftToLoad) this.loadDraft(this.draftToLoad)
		this.fileListeners()
	},
	beforeUnmount() {
		if(this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
	},
	methods: {
		debounce(fn, wait) {
			let timeout = null
			const debounced = (...args) => { clearTimeout(timeout); timeout = setTimeout(() => fn(...args), wait) }
			debounced.cancel = () => clearTimeout(timeout)
			return debounced
		},
		formatDatePart(value) {
			if(!value) return ''
			const m = moment(value, 'YYYY-MM-DD')
			return m.isValid() ? m.format('DD/MM/YYYY') : ''
		},
		syncStart() {
			if(this.startDatePart && this.startTimePart) {
				const combined = moment(`${this.startDatePart} ${this.startTimePart}`, DATETIME_FORMAT)
				if(combined.isValid() && !combined.isBefore(moment(), 'minute')) {
					this.newMeetingForm.start = combined.format(DATETIME_FORMAT)
				} else {
					// Defensa extra: si por algún cambio de fecha la hora quedó inválida, se limpia
					this.startTimePart = null
					this.newMeetingForm.start = null
					return
				}
			} else {
				this.newMeetingForm.start = null
			}
			this.validateEndAfterStart()
		},
		syncEnd() {
			if(this.endDatePart && this.endTimePart) {
				const combined = moment(`${this.endDatePart} ${this.endTimePart}`, DATETIME_FORMAT)
				if(this.newMeetingForm.start) {
					const start = moment(this.newMeetingForm.start, DATETIME_FORMAT)
					if(combined.isBefore(start)) {
						this.endDateError = 'La fecha de término no puede ser anterior a la de inicio'
						this.endTimePart = null
						this.newMeetingForm.end = null
						return
					}
				}
				this.endDateError = ''
				this.newMeetingForm.end = combined.format(DATETIME_FORMAT)
			} else {
				this.newMeetingForm.end = null
			}
		},
		validateEndAfterStart() {
			if(!this.newMeetingForm.end || !this.newMeetingForm.start) { this.endDateError = ''; return }
			const start = moment(this.newMeetingForm.start, DATETIME_FORMAT)
			const end = moment(this.newMeetingForm.end, DATETIME_FORMAT)
			if(end.isBefore(start)) {
				this.endDateError = 'La fecha de término no puede ser anterior a la de inicio'
				this.newMeetingForm.end = null
				this.endDatePart = null
				this.endTimePart = null
			} else {
				this.endDateError = ''
			}
		},
		async onPasteImage(file) { await this.createDraft(); this.onFileChanged(null, file) },
		onMentionsUpdated(userIds) { this.newInteractionMentions = userIds },
		handleFormChange() { if(!this.isResetting && this.handleFormChangeDebounced) this.handleFormChangeDebounced() },
		checkAndCreateDraft() { if(!this.draftCreated && !this.draftId) this.createDraft() },
		buildDraftData() {
			return {
				form: {
					...this.newMeetingForm,
					address: this.meetingModality === 'presencial' ? this.newMeetingForm.address : '',
					link: this.meetingModality === 'virtual' ? this.newMeetingForm.link : ''
				},
				participants: this.participants,
				meetingModality: this.meetingModality
			}
		},
		async createDraft() {
			if(this.draftCreated) return
			this.draftCreated = true
			const draftData = {
				type: 13,
				project_id: this.$store.projectsStore.state.selectedProject.id,
				data: JSON.stringify(this.buildDraftData())
			}
			try {
				const response = await createDraftAPI(draftData)
				if(response.code === 200) { this.draftId = response.draft.id; this.startDraftUpdateTimer(); return this.draftId }
				else if(response.offline) {
					this.$store.globalStore.dispatch('addMessage', {
						message: 'No hay conexión. El borrador se mantendrá localmente y podrás retomarlo cuando vuelvas a tener conexión desde la sección de borradores.',
						title: 'Borrador guardado localmente',
						created: new Date(),
						type: 'warning',
						icon: 'mdi-wifi-off',
						color: '#F59E0B'
					})
					this.draftId = response.draft.id
					this.startDraftUpdateTimer()
					return this.draftId
				}
			} catch(error) { console.error('Error al crear borrador:', error); this.draftCreated = false; throw error }
		},
		startDraftUpdateTimer() {
			if(this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
			this.draftSaveTimer = setInterval(() => this.updateDraft(), this.draftUpdateInterval * 1000)
		},
		async updateDraft() {
			if(!this.draftId) return
			try { await updateDraftAPI({ id: this.draftId, type: 13, data: JSON.stringify(this.buildDraftData()) }) }
			catch(error) { console.error('Error al actualizar borrador:', error) }
		},
		minimizeForm() {
			this.updateDraft()
			let title = 'Borrador Reunión: ' + (this.newMeetingForm.title || 'Sin Título')
			this.$store.globalStore.commit('addTaskToTasksHandler', {
				task_id: uuidv4(),
				title,
				type: 'Reunión',
				type_id: 12,
				number: null,
				newMeetingForm: this.newMeetingForm,
				draft_id: this.draftId
			})
			this.closeModalSelf()
		},
		closeModalSelf() {
			EventBus.$emit('reloadDraftList')
			if(this.handleFormChangeDebounced) this.handleFormChangeDebounced.cancel()
			this.updateDraft()
			this.isResetting = true
			if(this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
			this.closeModal()
			this.newMeetingForm = { title: '', description: '', start: null, end: null, responsible_id: null, address: '', link: '' }
			this.filesPreview = []; this.participants = []
			this.meetingModality = 'presencial'
			this.startDatePart = null; this.startTimePart = null; this.endDatePart = null; this.endTimePart = null
			this.endDateError = ''
			this.searchUsers = null; this.searchUsersParticipants = null
			this.usersToSelect = []; this.startDateMenu = false; this.endDateMenu = false
			this.loadingData = false; this.loadingUsers = false
			this.draftId = null; this.draftCreated = false
			this.$nextTick(() => setTimeout(() => { this.isResetting = false }, 100))
		},
		async loadDraft(draft) {
			if(!draft) return
			this.isResetting = true
			if(this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
			let draftData = draft.data
			if(typeof draftData === 'string') { try { draftData = JSON.parse(draftData) } catch(e) { this.isResetting = false; return } }
			if(draftData.form) this.newMeetingForm = {...this.newMeetingForm, ...draftData.form}
			if(draftData.participants) this.participants = [...draftData.participants]
			if(draftData.meetingModality) this.meetingModality = draftData.meetingModality
			if(this.newMeetingForm.start) {
				const startMoment = moment(this.newMeetingForm.start, DATETIME_FORMAT)
				if(startMoment.isValid()) { this.startDatePart = startMoment.format('YYYY-MM-DD'); this.startTimePart = startMoment.format('HH:mm') }
			}
			if(this.newMeetingForm.end) {
				const endMoment = moment(this.newMeetingForm.end, DATETIME_FORMAT)
				if(endMoment.isValid()) { this.endDatePart = endMoment.format('YYYY-MM-DD'); this.endTimePart = endMoment.format('HH:mm') }
			}
			try {
				const filePreviews = []
				if(draft.files && Array.isArray(draft.files)) {
					for(const file of draft.files) {
						if(file.mime_type && file.mime_type.includes('image')) filePreviews.push(file.pathmedia?.path ? {src: file.pathmedia.path, id: file.id, name: file.name} : {noImage: true, id: file.id, name: file.name})
						else filePreviews.push({noImage: true, id: file.id, name: file.name})
					}
				}
				this.filesPreview = filePreviews
			} catch(e) { console.error('Error al cargar archivos del draft:', e) }
			this.draftId = draft.id; this.draftCreated = true; this.startDraftUpdateTimer()
			this.$nextTick(() => { this.isResetting = false })
			this.$store.globalStore.dispatch('addMessage', { message: 'Borrador cargado correctamente.', title: 'Borrador Cargado', created: new Date(), type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3' })
			this.$store.consultasStore.commit('setDraftToLoad', null)
		},
		getUserByID(userID) { return userID ? (this.$store.usersStore.state.usersList.find(u => u.id === userID) || null) : null },
		initials(name) { return (name || '').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') },
		removeParticipantByID(id) {
			const idx = this.participants.indexOf(id)
			if(idx !== -1) this.participants.splice(idx, 1)
		},
		getFinalUserList(participants = false) {
			if(this.checkPermissions(['assign_responsable_meeting'])) return participants ? this.$store.usersStore.state.usersList.filter(u => u.id !== this.newMeetingForm.responsible_id) : this.$store.usersStore.state.usersList
			return this.$store.usersStore.state.usersList.filter(u => participants ? u.id !== this.newMeetingForm.responsible_id : u && ((u.projectjob && u.projectjob.job_type_id == 2) || u.id == this.$store.userStore.state.userInfo.id))
		},
		fileListeners() {
			EventBus.$on('completedFileUpload', async (payload) => {
				if(payload && payload.draft_id === this.draftId) {
					const findIndex = this.filesPreview.findIndex(f => f.uuid === payload.uuid)
					if(findIndex !== -1) this.$set(this.filesPreview[findIndex], 'id', payload.file.id)
				}
			})
		},
		async getUsersList() { this.$store.usersStore.dispatch('getUsersList', { project_id: this.$store.projectsStore.state.selectedProject.id, start: 0, limit: 20 }) },
		async getMeetingsList() {
			let project_id = this.$store.projectsStore.state.selectedProject?.id || parseInt(localStorage.getItem('selectedProjectID'))
			if(project_id) this.$store.consultasStore.dispatch('getMyMeetings', {project_id})
		},
		async searchUsersLocal(searchString) {
			this.loadingUsers = true
			this.usersToSelect = this.$store.usersStore.state.usersList.filter(u => u.name.includes(searchString) || u.email.includes(searchString))
			this.loadingUsers = false
		},
		usersFilter(item, queryText) {
			const q = queryText.toLowerCase()
			return item.name.toLowerCase().includes(q) || item.email.toLowerCase().includes(q)
		},
		onButtonClick() { this.$refs.uploader.click() },
		async onFilesChanged(e) {
			await this.createDraft();
			const files = e?.target?.files || []
			for (let i = 0; i < files.length; i++) {
				await this.onFileChanged(null, files[i])
			}
		},
		async onFileChanged(e, file = null) {
			const newFile = e?.target?.files[0] || file
			if(!newFile) return
			if(!this.draftId && !this.draftCreated) await this.createDraft()
			const uuid = uuidv4()
			if(newFile.type.includes('image')) { const b64 = await this.toBase64(newFile); this.filesPreview.push({src: b64, name: newFile.name, uuid}) }
			else this.filesPreview.push({noImage: true, name: newFile.name, uuid})
			this.$store.globalStore.commit('setFilesToUploadCenter', { type: 13, payload: {draft_id: this.draftId, project_id: this.$store.projectsStore.state.selectedProject.id, uuid}, description: this.newMeetingForm.title, total: newFile.size, uuid, file: newFile })
		},
		removeAttachment(file, index) { this.filesPreview.splice(index, 1); if(file.id) deleteFile({file_id: file.id}) },
		toBase64(file) { return new Promise(resolve => { const r = new FileReader(); r.readAsDataURL(file); r.onload = () => resolve(r.result); r.onerror = () => resolve(null) }) },
		preCreateMeeting() {
			if(this.inCancelTime) { clearInterval(this.cancelInterval); this.confirmButtonText = 'Confirmar'; this.inCancelTime = false; return null }
			if(!this.newMeetingForm.description) {
				this.$store.globalStore.dispatch('addMessage', { message: 'Por favor escribe una descripción.', title: 'Error', created: new Date(), type: 'success', icon: 'mdi-alert-circle', color: '#fb8c00' })
				return false
			}
			if(!this.newMeetingForm.start) {
				this.$store.globalStore.dispatch('addMessage', { message: 'Por favor selecciona la fecha y hora de inicio.', title: 'Error', created: new Date(), type: 'warning', icon: 'mdi-alert-circle', color: '#fb8c00' })
				return false
			}
			if(this.endDateError) {
				this.$store.globalStore.dispatch('addMessage', { message: this.endDateError, title: 'Error', created: new Date(), type: 'warning', icon: 'mdi-alert-circle', color: '#fb8c00' })
				return false
			}
			if(this.$refs.form.validate()) {
				this.inCancelTime = true; this.cancelTimer = 7; this.confirmButtonText = `Cancelar (${this.cancelTimer})`
				this.cancelInterval = setInterval(() => {
					this.cancelTimer--; this.confirmButtonText = `Cancelar (${this.cancelTimer})`
					if(this.cancelTimer == 0) { this.inCancelTime = false; clearInterval(this.cancelInterval); this.createMeeting() }
				}, 1000)
			}
		},
		async saveDraftAndClose() {
			try {
				this.loadingData = true
				await this.updateDraft()
				this.$store.globalStore.dispatch('addMessage', {
					message: 'Borrador guardado correctamente.',
					title: 'Borrador Guardado',
					type: 'success',
					icon: 'mdi-check-circle',
					color: '#3EBFA3',
					created: new Date()
				})
				this.closeModalSelf()
			} catch(error) {
				console.error('Error al guardar borrador:', error)
				this.$store.globalStore.dispatch('addMessage', {
					message: 'Error al guardar el borrador. Por favor, intenta de nuevo.',
					title: 'Error',
					type: 'error',
					icon: 'mdi-alert-circle',
					color: '#fb8c00',
					created: new Date()
				})
			} finally {
				this.loadingData = false
			}
		},
		async createMeeting() {
			this.loadingData = true; this.confirmButtonText = 'Confirmar'
			const payload = {
				project_id: this.$store.projectsStore.state.selectedProject.id,
				title: this.newMeetingForm.title,
				description: this.newMeetingForm.description,
				start: this.newMeetingForm.start,
				end: this.newMeetingForm.end,
				responsible_id: this.newMeetingForm.responsible_id,
				address: this.meetingModality === 'presencial' ? this.newMeetingForm.address : null,
				link: this.meetingModality === 'virtual' ? this.newMeetingForm.link : null,
				draft_id: this.draftId,
				participants: JSON.stringify(this.participants)
			}
			if(this.newInteractionMentions.length > 0) payload.mentions = this.newInteractionMentions
			const meeting = await create(payload)
			if(meeting.code === 200) {
				if(this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
				this.getMeetingsList();
				this.closeModalSelf();
				EventBus.$emit('onProjectElementsChange')
				EventBus.$emit('reloadMeetings')
				this.$store.globalStore.dispatch('addMessage', { message: 'Haz click aquí para visualizarla.', title: 'Se ha creado la reunión/minuta #' + meeting.meeting.number, created: new Date(), type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3', action: 'meeting', meeting_id: meeting.meeting.id })
				this.viewMeeting(meeting.meeting.id)
			}
			else{
				this.saveDraftAndClose()
			}
			this.loadingData = false
		},
		async viewMeeting(meeting_id){
			this.$store.meetingStore.commit('setCurrentMeeting', {loading: true})
			const meeting = await getMeeting({meeting_id})
			if(meeting.code === 200){
				this.$store.meetingStore.commit('setCurrentMeeting', meeting.meeting)
			}
		},
	}
}
</script>

<style scoped>
/* ── Variables ── */
:root {
	--primary: #2D4B95;
	--primary-light: #5E85EF;
	--border: #E5E7EB;
	--text-secondary: #6B7280;
	--bg-surface: #F9FAFB;
	--radius: 8px;
}

/* ── Modal Card ── */
.modal-card {
	border-radius: 12px !important;
	overflow: hidden;
}

/* ── Header ── */
.modal-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 14px 20px;
	background: #2D4B95;
	border-bottom: 1px solid rgba(255,255,255,0.1);
}
.modal-header-left {
	display: flex;
	align-items: center;
	gap: 10px;
}
.modal-icon {
	width: 30px;
	height: 30px;
	border-radius: 8px;
	background: rgba(255,255,255,0.15);
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}
.modal-title {
	margin: 0;
	font-size: 14px;
	font-weight: 600;
	color: white;
	line-height: 1.2;
}
.modal-subtitle {
	margin: 0;
	font-size: 11px;
	color: rgba(255,255,255,0.65);
	line-height: 1.2;
}
.modal-subtitle .separator {
	margin: 0 5px;
	opacity: 0.5;
}
.modal-header-actions {
	display: flex;
	gap: 6px;
}
.header-btn {
	width: 26px;
	height: 26px;
	border-radius: 6px;
	border: none;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background 0.15s;
}
.header-btn.minimize { background: rgba(251, 140, 0,0.9); color: white; }
.header-btn.minimize:hover { background: rgba(251, 140, 0,1); }
.header-btn.close { background: rgba(244,81,108,0.9); color: white; }
.header-btn.close:hover { background: rgba(244,81,108,1); }

/* ── Body ── */
.modal-body {
	overflow-y: auto;
	max-height: calc(90vh - 80px);
}

/* ── Form Sections ── */
.form-section {
	padding: 14px 20px;
	border-bottom: 1px solid #F3F4F6;
}
.form-section:last-child { border-bottom: none; }

.field-label {
	display: block;
	font-size: 11px;
	font-weight: 600;
	color: #2D4B95;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	margin-bottom: 6px;
}

/* ── Grids ── */
.fields-grid {
	display: grid;
	gap: 12px;
}
.fields-grid.two-col { grid-template-columns: 1fr 1fr; }
.fields-grid.four-col { grid-template-columns: 1fr 1fr 1fr 1fr; }
.field-group { display: flex; flex-direction: column; }

@media (max-width: 900px) {
	.fields-grid.four-col { grid-template-columns: 1fr 1fr; }
}

@media (max-width: 600px) {
	.fields-grid.two-col { grid-template-columns: 1fr; }
	.fields-grid.four-col { grid-template-columns: 1fr 1fr; }
}

.field-error {
	margin: 8px 0 0;
	font-size: 11px;
	color: #F4516C;
	font-weight: 500;
}

/* ── Modalidad ── */
.modality-group {
	display: flex;
	gap: 6px;
	margin-bottom: 10px;
}
.modality-btn {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 6px 14px;
	border-radius: 7px;
	border: 1.5px solid #E5E7EB;
	background: white;
	cursor: pointer;
	font-size: 12px;
	font-weight: 500;
	color: #374151;
	transition: all 0.15s;
}
.modality-btn:hover { border-color: #5E85EF80; }
.modality-btn.active {
	background: #5E85EF;
	border-color: #5E85EF;
	color: white;
}
.modality-field { }

/* ── Attachments ── */
.attachments-row {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-top: 8px;
	flex-wrap: wrap;
}
.previews { display: flex; gap: 6px; flex-wrap: wrap; flex: 1; }
.preview-img-wrap { position: relative; }
.preview-thumb { width: 40px; height: 40px; border-radius: 6px; object-fit: cover; display: block; }
.preview-remove {
	position: absolute;
	top: -4px; right: -4px;
	width: 16px; height: 16px;
	border-radius: 50%;
	background: #F4516C;
	border: none;
	cursor: pointer;
	display: flex; align-items: center; justify-content: center;
}
.preview-file {
	display: flex; align-items: center; gap: 4px;
	padding: 3px 8px;
	border-radius: 5px;
	background: #EEF2FF;
	font-size: 11px;
	color: #4B5563;
}
.preview-file button { background: none; border: none; cursor: pointer; display: flex; align-items: center; }
.attach-btn {
	display: flex; align-items: center; gap: 5px;
	padding: 5px 12px;
	border-radius: 6px;
	border: 1.5px solid #E5E7EB;
	background: white;
	cursor: pointer;
	font-size: 12px;
	color: #5E85EF;
	font-weight: 500;
	transition: border-color 0.15s;
	white-space: nowrap;
}
.attach-btn:hover { border-color: #5E85EF; }

/* ── Participants ── */
.participant-list { display: flex; flex-direction: column; gap: 4px; }
.participant-card {
	display: flex; align-items: center; justify-content: space-between;
	padding: 6px 10px;
	background: #F9FAFB;
	border: 1px solid #E5E7EB;
	border-radius: 8px;
	gap: 8px;
}
.participant-left { display: flex; align-items: center; gap: 8px; flex: 1; min-width: 0; }
.p-avatar {
	width: 30px; height: 30px; border-radius: 50%;
	background: #E0E7FF;
	display: flex; align-items: center; justify-content: center;
	font-size: 11px; font-weight: 600; color: #4F46E5;
	flex-shrink: 0; overflow: hidden;
}
.p-avatar img { width: 100%; height: 100%; object-fit: cover; }
.p-info { display: flex; flex-direction: column; min-width: 0; }
.p-name { font-size: 12px; font-weight: 500; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.p-role { font-size: 11px; color: #6B7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.participant-right { display: flex; align-items: center; gap: 5px; flex-shrink: 0; }
.p-remove-btn {
	display: flex; align-items: center; justify-content: center;
	padding: 3px; border: none; background: transparent; cursor: pointer;
	border-radius: 4px; transition: background 0.15s;
}
.p-remove-btn:hover { background: #FEE2E2; }

/* ── Footer ── */
.modal-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 20px;
	background: #F9FAFB;
	border-top: 1px solid #E5E7EB;
}
.footer-actions { display: flex; gap: 8px; align-items: center; }
.btn-cancel {
	padding: 7px 16px;
	border-radius: 7px;
	border: 1.5px solid #E5E7EB;
	background: white;
	cursor: pointer;
	font-size: 13px;
	font-weight: 500;
	color: #374151;
	transition: all 0.15s;
}
.btn-cancel:hover { border-color: #D1D5DB; background: #F3F4F6; }
.btn-confirm {
	padding: 7px 20px;
	border-radius: 7px;
	border: none;
	background: #2D4B95;
	cursor: pointer;
	font-size: 13px;
	font-weight: 600;
	color: white;
	display: flex; align-items: center;
	transition: all 0.15s;
}
.btn-confirm:hover { background: #253d7a; }
.btn-confirm.canceling { background: #F4516C; }
.btn-confirm:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Misc ── */
.light-blue-text { color: #5E85EF; }
/deep/ .remove-bottom-details .v-text-field__details { display: none; }
/deep/ .remove-bottom-details .v-input__slot { margin-bottom: 0; }

/* ── Transitions ── */
.fade-slide-enter-active, .fade-slide-leave-active { transition: opacity 0.2s ease, transform 0.2s ease; }
.fade-slide-enter, .fade-slide-leave-to { opacity: 0; transform: translateY(-4px); }
</style>